import React, { useContext, useState } from 'react';
import { SiteContext } from '../../../context/SiteContext';
import TextInput from '../../common/TextInput/TextInput';
import { CommonValidation } from '../../common/CommonValidation/commonValidation';
import { ButtonContinue } from '../../common/Button/ButtonContinue';
import './NeedHelpPage.scss';
import { ButtonBack } from '../../common/Button/ButtonBack';
import { useNavigate } from 'react-router-dom';
import Links from '../../common/Widgets/Links';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faChevronLeft } from '@fortawesome/free-solid-svg-icons';
import DateSelector from '../../common/DateSelector/DateSelector';
import { getInsuranceJourneyMinMaxDob } from '../../common/HelperMethods';
import useCarInsuranceController from '../../../hooks/insuranceJourney/useCarInsuranceController';
import {
  PageAbsolutePath,
  InsuranceRetrievingMessage
} from '../../../consts/Common';
import LoadingContainer from '../../common/LoadingContainer/LoadingContainer';
import { MarketingPreferencesResponse } from '../../../api/Models/InsuranceJourney';
import { useErrorBoundary } from 'react-error-boundary';

type UpdatePreferencesFields = {
  firstName: string;
  lastName: string;
  dateOfBirth: Date | undefined;
  emailAddress: string;
  postCode: string;
};

type UpdatePreferencesErrors = {
  firstName: string;
  lastName: string;
  dateOfBirth: string | undefined;
  emailAddress: string;
  postCode: string;
};

const initialFormValues: UpdatePreferencesFields = {
  firstName: '',
  lastName: '',
  dateOfBirth: undefined,
  emailAddress: '',
  postCode: ''
};

const initialFormErrors: UpdatePreferencesErrors = {
  firstName: '',
  lastName: '',
  dateOfBirth: undefined,
  emailAddress: '',
  postCode: ''
};

const UpdatePreferencesPage = (): JSX.Element => {
  const navigate = useNavigate();
  const { showBoundary } = useErrorBoundary();
  const { site } = useContext(SiteContext);
  const { brandPhoneNumbers } = site.brand;
  const { quotesAndRenewalsNumber } = brandPhoneNumbers;
  const [formValues, setFormValues] =
    useState<UpdatePreferencesFields>(initialFormValues);
  const [formErrors, setFormErrors] =
    useState<UpdatePreferencesErrors>(initialFormErrors);
  const { firstName, lastName, emailAddress, dateOfBirth, postCode } =
    formValues;
  const [showTwoControlError, setShowTwoControlError] = useState(false);
  const [dobIsValid, setDobIsValid] = useState(true);
  const [updateDobError, setUpdateDobError] = useState(false);
  const [showDetailsNotFound, setShowDetailsNotFound] = useState(false);
  const [loading, setLoading] = useState(false);

  const { minDob, maxDob } = getInsuranceJourneyMinMaxDob();

  const { getMarketingPreferences } = useCarInsuranceController();

  const onBackendFailure = (err: string) => {
    showBoundary(new Error(err));
  };

  const goBack = (): void => {
    navigate(-1);
  };

  const onDobValidated = (isValid: boolean): void => {
    setUpdateDobError(false);
    setDobIsValid(isValid);
  };

  const validatePageDetails = (): boolean => {
    let isValid = true;

    const lastNameMinCharLimit = 3;
    const firstNameMinCharLimit = 3;
    const updatedFormErrors = { ...initialFormErrors };

    if (CommonValidation.isEmpty(firstName)) {
      isValid = false;
      updatedFormErrors.firstName = 'First name is required';
    } else if (firstName.length < firstNameMinCharLimit) {
      isValid = false;
      updatedFormErrors.firstName = `First name must have at least ${firstNameMinCharLimit} characters`;
    }

    if (CommonValidation.isEmpty(lastName)) {
      isValid = false;
      updatedFormErrors.lastName = 'Last name is required';
    } else if (lastName.length < lastNameMinCharLimit) {
      isValid = false;
      updatedFormErrors.lastName = `Last name must have at least ${lastNameMinCharLimit} characters`;
    }

    if (CommonValidation.isNotEmpty(emailAddress)) {
      if (!CommonValidation.isValidEmail(emailAddress)) {
        updatedFormErrors.emailAddress = 'Email address is invalid';
        isValid = false;
      }
    }

    if (
      !CommonValidation.isEmpty(postCode) &&
      !CommonValidation.isValidPostcode(postCode)
    ) {
      updatedFormErrors.postCode = 'Postcode is invalid';
      isValid = false;
    }

    if (dobIsValid === false) {
      isValid = false;
    }

    if (isValid && dobIsValid) {
      if (
        (CommonValidation.isEmpty(emailAddress) &&
          CommonValidation.isEmpty(postCode)) ||
        (CommonValidation.isEmpty(emailAddress) && dateOfBirth == undefined) ||
        (dateOfBirth == undefined && CommonValidation.isEmpty(postCode))
      ) {
        setShowTwoControlError(true);
        isValid = false;
      } else {
        setShowTwoControlError(false);
      }
    } else {
      setShowTwoControlError(false);
    }
    setFormErrors(updatedFormErrors);
    return isValid;
  };

  const onTextChange = (e: React.FormEvent<HTMLInputElement>, key: string) => {
    const updatedFormValues = { ...formValues, [key]: e.currentTarget.value };

    setFormValues(updatedFormValues);
  };

  const onDateChange = (value: Date | undefined) => {
    const updatedFormValues = { ...formValues, dateOfBirth: value };

    setFormValues(updatedFormValues);
  };

  const onClickProceed = () => {
    const isValid = validatePageDetails();
    if (isValid) {
      setLoading(true);
      getMarketingPreferences(
        {
          firstName,
          lastName,
          dateOfBirth,
          postCode,
          emailAddress,
          tiaProductCode: site.brand.tiaProductCode
        },
        site.user.token,
        getMarketingPreferencesSuccess,
        onBackendFailure
      );
    }
  };

  const getMarketingPreferencesSuccess = (
    response: MarketingPreferencesResponse
  ) => {
    if (response.marketingPreferences.partnerId === 0) {
      setShowDetailsNotFound(true);
      setLoading(false);
    } else {
      navigate(PageAbsolutePath.Insurance.PostcodeConfirmationPage, {
        state: response
      });
    }
  };

  const chevronLeftComponent = <FontAwesomeIcon icon={faChevronLeft} />;

  return (
    <div>
      <LoadingContainer loading={loading} message={InsuranceRetrievingMessage}>
        <Links
          id='back-link'
          title=''
          items={[{ to: '', text: 'Back', onClick: goBack }]}
          icon={chevronLeftComponent}
        />
        <h4 className='section-header'>Update your marketing preferences</h4>
        <div className='table-flex-layout'>
          {showDetailsNotFound && (
            <div>
              <h4>Sorry, we can't find your details</h4>
              <div>
                Try checking your details again, or give us a call on{' '}
                {quotesAndRenewalsNumber} and we'll be happy to help.
              </div>
            </div>
          )}
          <div className='form-container'>
            <div className='one-col'>
              <div className='control-label'>First name</div>
              <div className='control'>
                <TextInput
                  name='firstname'
                  placeholder='First name'
                  value={firstName}
                  onChange={(e: React.FormEvent<HTMLInputElement>) =>
                    onTextChange(e, 'firstName')
                  }
                  error={formErrors.firstName}
                />
              </div>
            </div>
            <div className='one-col'>
              <div className='control-label'>Last name</div>
              <div className='control'>
                <TextInput
                  name='lastname'
                  placeholder='Last name'
                  value={lastName}
                  onChange={(e: React.FormEvent<HTMLInputElement>) =>
                    onTextChange(e, 'lastName')
                  }
                  error={formErrors.lastName}
                />
              </div>
            </div>
            <div className='one-col'>
              <div className='control-label'>
                Please complete <u>TWO</u> of the following fields
              </div>
              <div className='control-label'>Date of birth</div>
              <div className='control'>
                <DateSelector
                  value={dateOfBirth}
                  minDate={minDob}
                  maxDate={maxDob}
                  invalidDateMessage={formErrors.dateOfBirth}
                  updateDobError={updateDobError}
                  culture={site.cultureCode}
                  onChange={onDateChange}
                  onValidated={(isValid) => onDobValidated(isValid)}
                  name={'dob'}
                />
              </div>
            </div>
            <div className='one-col'>
              <div className='control-label'>Postcode</div>
              <div className='control'>
                <TextInput
                  name='postcode'
                  placeholder='Postcode'
                  value={postCode}
                  onChange={(e: React.FormEvent<HTMLInputElement>) =>
                    onTextChange(e, 'postCode')
                  }
                  error={formErrors.postCode}
                />
              </div>
            </div>
            <div className='one-col'>
              <div className='control-label'>Email address</div>
              <div className='control'>
                <TextInput
                  name='emailaddress'
                  placeholder='Email address'
                  value={emailAddress}
                  onChange={(e: React.FormEvent<HTMLInputElement>) =>
                    onTextChange(e, 'emailAddress')
                  }
                  error={formErrors.emailAddress}
                />
              </div>
            </div>
            {showTwoControlError && (
              <div className='one-col'>
                <h4 className='retrieveQuoteErrorText'>
                  Please complete TWO of the fields above
                </h4>
              </div>
            )}
          </div>
          <div className='flex-row form-nav'>
            <ButtonBack
              name='btnBack'
              text='Back'
              onClick={goBack}
              additionalClassNames='btn-back'
            />
            <ButtonContinue
              name='btnProceed'
              text='Proceed'
              onClick={onClickProceed}
              additionalClassNames='btn-continue'
            />
          </div>
        </div>
      </LoadingContainer>
    </div>
  );
};

export default UpdatePreferencesPage;
